<template>
  <Teleport to="body">
    <Transition name="mobile-nav-panel">
      <div
        v-if="open"
        class="mobile-nav-panel m-only"
        :style="{ '--mobile-nav-panel-top': `${topOffset}px` }"
      >
        <div class="mobile-nav-panel__surface">
          <div class="mobile-nav-panel__top">
            <button type="button" class="mobile-nav-panel__logo-button" @click="emit('logo-click')">
              <Resource :src="logoUrl" class="mobile-nav-panel__logo-img" alt="logo" />
            </button>
            <Close class="mobile-nav-panel__close" @click="setOpen(false)" />
          </div>

          <div class="mobile-nav-panel__list x-scrollbar">
            <template v-for="section in sections" :key="section.key">
              <NuxtLink
                v-if="!section.groups.length"
                :to="section.route || section.link || '#'"
                :target="section.target"
                class="mobile-nav-panel__entry"
                @click="handleNavClick(section)"
              >
                <span class="mobile-nav-panel__entry-text">{{ section.name }}</span>
              </NuxtLink>

              <section v-else class="mobile-nav-panel__accordion">
                <div class="mobile-nav-panel__accordion-header">
                  <NuxtLink
                    v-if="section.route"
                    :to="section.route"
                    :target="section.target"
                    class="mobile-nav-panel__accordion-link"
                    @click="handleNavClick(section)"
                  >
                    <span class="mobile-nav-panel__entry-text">{{ section.name }}</span>
                  </NuxtLink>
                  <button
                    v-else
                    type="button"
                    class="mobile-nav-panel__accordion-link"
                    @click="toggleSection(section.key)"
                  >
                    <span class="mobile-nav-panel__entry-text">{{ section.name }}</span>
                  </button>

                  <button
                    type="button"
                    class="mobile-nav-panel__accordion-toggle"
                    :aria-expanded="expandedKey === section.key"
                    :aria-controls="getAccordionPanelId(section.key)"
                    @click="toggleSection(section.key)"
                  >
                    <DropdownIcon
                      class="mobile-nav-panel__accordion-icon"
                      :class="{
                        'mobile-nav-panel__accordion-icon--expanded': expandedKey === section.key,
                      }"
                    />
                  </button>
                </div>

                <Transition
                  name="mobile-nav-panel-accordion"
                  @before-enter="onAccordionBeforeEnter"
                  @enter="onAccordionEnter"
                  @after-enter="onAccordionAfterEnter"
                  @before-leave="onAccordionBeforeLeave"
                  @leave="onAccordionLeave"
                  @after-leave="onAccordionAfterLeave"
                >
                  <div
                    v-if="expandedKey === section.key"
                    :id="getAccordionPanelId(section.key)"
                    class="mobile-nav-panel__accordion-body"
                    :class="{
                      'mobile-nav-panel__accordion-body--columns':
                        section.pageKey === PageKey.SCENARIOS,
                    }"
                  >
                    <div
                      v-for="(group, groupIndex) in section.groups"
                      :key="`${section.key}-${groupIndex}`"
                      class="mobile-nav-panel__group"
                      :class="{
                        'mobile-nav-panel__group--column': section.pageKey === PageKey.SCENARIOS,
                      }"
                    >
                      <div v-if="group.title" class="mobile-nav-panel__group-title">
                        {{ group.title }}
                      </div>

                      <NuxtLink
                        v-for="item in group.items"
                        :key="item.route || item.link || item.name"
                        :to="item.route || item.link || '#'"
                        :target="item.target"
                        class="mobile-nav-panel__child-link"
                        @click="handleNavClick(item)"
                      >
                        <span class="mobile-nav-panel__child-text">{{ item.name }}</span>
                      </NuxtLink>
                    </div>
                  </div>
                </Transition>
              </section>
            </template>
          </div>

          <div class="mobile-nav-panel__footer">
            <div class="mobile-nav-panel__footer-inner">
              <slot name="footer">
                <Button
                  variant="purple"
                  size="large"
                  blank
                  class="mobile-nav-panel__footer-button"
                  @click="handleFooterAction"
                >
                  {{ footerButtonText }}
                </Button>
              </slot>

              <div v-if="userInfo?.user_id" class="mobile-nav-panel__footer-tip">
                {{
                  $t(
                    'dian-ji-fu-zhi-you-yan-guan-wang-lian-jie-huo-shi-yong-dian-nao-liu-lan-qi-sou-suo-you-yan-deng-lu',
                  )
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { message } from 'ant-design-vue';
import Button from '@rendora/design/components/button/Button.vue';
import DropdownIcon from '~/assets/svg/icon-dropdown.svg?component';
import Close from '~/design/icons/close.svg?component';
import type { Nav } from '~/hooks/useNav';
import { PageKey } from '~/constants/page';
import { goLogin } from '~/utils/permission';
import { copy } from '~/utils/copy';
import type { MenuGroup } from './TopDropdown.vue';
import { collectActiveNavKeys, getNavActiveKey } from './nav-active';

interface MobileNavSection extends Nav {
  key: string;
  groups: MenuGroup[];
}

interface Props {
  open?: boolean;
  navs?: Nav[];
  scenariosGroups?: MenuGroup[];
  logoUrl?: string;
  topOffset?: number;
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
  navs: () => [],
  scenariosGroups: () => [],
  logoUrl: '',
  topOffset: 0,
});

const emit = defineEmits<{
  'update:open': [value: boolean];
  'logo-click': [];
}>();

const route = useRoute();
const userInfo = useUserInfo();
const expandedKey = ref<string | null>(null);

const sections = computed<MobileNavSection[]>(() =>
  props.navs.map(nav => ({
    ...nav,
    key: nav.pageKey || nav.route || nav.link || nav.name,
    groups:
      nav.pageKey === PageKey.SCENARIOS
        ? props.scenariosGroups.filter(group => group.items.length > 0)
        : nav.children?.length
          ? [{ items: nav.children }]
          : [],
  })),
);
const activeNavKeys = computed(() => {
  return collectActiveNavKeys(sections.value, route.name, route.path);
});

const setOpen = (value: boolean) => {
  emit('update:open', value);
};

const footerButtonText = computed(() =>
  userInfo.value?.user_id ? '前往电脑端 体验完整功能' : '免费注册',
);

const toggleSection = (key: string) => {
  expandedKey.value = expandedKey.value === key ? null : key;
};

const getAccordionPanelId = (key: string) => `mobile-nav-panel-${key}`;

const isNavActive = (nav: Nav) => {
  return activeNavKeys.value.has(getNavActiveKey(nav));
};

const getDefaultExpandedKey = () => {
  const activeSection = sections.value.find(
    section => section.groups.length && isNavActive(section),
  );

  return activeSection?.key || null;
};

const lockScroll = (locked: boolean) => {
  if (!import.meta.client) {
    return;
  }

  document.body.style.overflow = locked ? 'hidden' : '';
  document.documentElement.style.overflow = locked ? 'hidden' : '';
};

const handleNavClick = (nav: Nav) => {
  if (nav.trackEvent) {
    trackEvent(nav.trackEvent);
  }

  setOpen(false);
};

const handleFooterAction = async () => {
  if (!userInfo.value?.user_id) {
    setOpen(false);
    goLogin({ redirect: encodeURIComponent(encodeURIComponent(location.href)) });
    return;
  }

  const url = window.location.origin;

  try {
    await copy(url);
    message.success('成功复制链接，请使用电脑登录');
  } catch (_error) {
    message.info('链接获取失败，请使用电脑浏览器搜索“有言”登录使用');
  }

  setOpen(false);
};

const withAccordionPanel = (el: Element, updater: (panel: HTMLElement) => void) => {
  try {
    const panel = el as HTMLElement | null;

    if (!panel) {
      return;
    }

    updater(panel);
  } catch (_error) {}
};

const onAccordionBeforeEnter = (el: Element) => {
  withAccordionPanel(el, panel => {
    panel.style.height = '0px';
    panel.style.opacity = '0';
  });
};

const onAccordionEnter = (el: Element) => {
  withAccordionPanel(el, panel => {
    panel.style.height = `${panel.scrollHeight}px`;
    panel.style.opacity = '1';
  });
};

const onAccordionAfterEnter = (el: Element) => {
  withAccordionPanel(el, panel => {
    panel.style.height = 'auto';
  });
};

const onAccordionBeforeLeave = (el: Element) => {
  withAccordionPanel(el, panel => {
    panel.style.height = `${panel.scrollHeight}px`;
    panel.style.opacity = '1';
  });
};

const onAccordionLeave = (el: Element) => {
  withAccordionPanel(el, panel => {
    void panel.offsetHeight;
    panel.style.height = '0px';
    panel.style.opacity = '0';
  });
};

const onAccordionAfterLeave = (el: Element) => {
  withAccordionPanel(el, panel => {
    panel.style.height = '';
    panel.style.opacity = '';
  });
};

watch(
  () => props.open,
  value => {
    lockScroll(value);
    expandedKey.value = value ? getDefaultExpandedKey() : null;
  },
  { immediate: true },
);

watch(
  () => route.fullPath,
  () => {
    setOpen(false);
  },
);

onBeforeUnmount(() => {
  lockScroll(false);
});
</script>

<style scoped lang="scss" src="./styles/panel.mobile.scss" />
